import React, { useEffect, useRef, useState } from 'react';
import { Form, Input, Button, InputNumber, DatePicker, Select, Row, Col, message } from 'antd';
import { useSupplierPayment } from '../../store/supplierPaymentContext';
import { useSupplier } from '../../store/supplierContext';
import { useCurrency } from '../../store/currencyContext';
import dayjs from 'dayjs';
import { SupplierPayment } from '../../types/supplierPayment';
import { useTranslation } from "react-i18next";
import { useReactToPrint } from 'react-to-print';
import PaymentInvoice from '../Print/PaymentInvoice';

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
    { value: "other", label: "Other" },
];

interface Props {
    editSupplierPayment?: SupplierPayment;
    onFinishEdit?: () => void;
}

const SupplierPaymentForm: React.FC<Props> = ({ editSupplierPayment, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addSupplierPayment, updateSupplierPayment } = useSupplierPayment();
    const { suppliers, fetchSuppliers } = useSupplier();
    const { currencies } = useCurrency();
    const { t } = useTranslation();

    // === PRINT LOGIC ===
    const [newPayment, setNewPayment] = useState<SupplierPayment | null>(null);
    const printRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: t('supplier_payment_invoice') as string,
    });

    const currencyList = currencies.map(c => ({
        value: c.code,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code
    }));

    // --- ADDED: Track selected supplier ---
    const [selectedSupplier, setSelectedSupplier] = useState<any>(null);

    useEffect(() => {
        fetchSuppliers();
        const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
        if (editSupplierPayment) {
            form.setFieldsValue({
                ...editSupplierPayment,
                date: editSupplierPayment.date ? dayjs(editSupplierPayment.date) : dayjs()
            });
            if (editSupplierPayment.supplier) {
                const found = suppliers.find(s => s.id === editSupplierPayment.supplier);
                setSelectedSupplier(found || null);
            }
        } else {
            form.setFieldsValue({ date: dayjs(), amount: 0, currency: DEFAULT_CURRENCY });
            setSelectedSupplier(null);
        }
        // eslint-disable-next-line
    }, []);

    const supplierOptions = (suppliers || []).map(s => ({
        value: s.id,
        label: `${s.name} (${s.phone || ""})`
    }));

    // --- ADDED: Supplier select change handler ---
    const handleSupplierChange = (id: string) => {
        const found = suppliers.find(s => s.id === id);
        setSelectedSupplier(found || null);
        // If switching to select, clear direct input
        if (found) {
            form.setFieldsValue({ supplierName: "", supplierPhone: "" });
        }
    };

    // Helper to get supplier name
    const getSupplierName = (supplierId?: string, supplierName?: string): string => {
        if (supplierId) {
            const found = suppliers.find(s => s.id === supplierId);
            return found?.name || "-";
        }
        return supplierName || "-";
    };

    // Payment method label helper
    const getPaymentMethodLabel = (code?: string) => {
        return PAYMENT_METHODS.find(m => m.value === code)?.label || "-";
    };

    const onFinish = (values: any) => {
        if (!values.amount || values.amount <= 0) {
            message.error(t('enter_valid_amount'));
            return;
        }
        const formatted = {
            ...values,
            date: values.date ? values.date.toISOString() : undefined,
        };
        if (editSupplierPayment) {
            updateSupplierPayment({ ...editSupplierPayment, ...formatted });
            onFinishEdit?.();
        } else {
            addSupplierPayment(formatted).then((created: SupplierPayment) => {
                setNewPayment(created);
                setTimeout(() => handlePrint(), 400); // Print after save
                form.resetFields();
                const DEFAULT_CURRENCY = currencies.find(c => c.isDefault)?.code || "IQD";
                form.setFieldsValue({ currency: DEFAULT_CURRENCY });
            });
        }
    };

    return (
        <>
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ maxWidth: 600, margin: '0 auto', background: "#fff", padding: 24, borderRadius: 10 }}
            autoComplete="off"
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="supplier" label={t('supplier')}>
                        <Select
                            options={supplierOptions}
                            placeholder={t('select_supplier')}
                            allowClear
                                showSearch
                                optionFilterProp="label"
                                onChange={handleSupplierChange}
                        />
                            {/* --- ADDED: Supplier balances display --- */}
                            {selectedSupplier && (
                                <div style={{
                                    marginTop: 8,
                                    background: '#f6fafd',
                                    borderRadius: 12,
                                    padding: '10px 15px',
                                    boxShadow: '0 1px 4px #e9e9e9'
                                }}>
                                    <b>{t('balances')}:</b>
                                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                                        {(selectedSupplier.balances || []).map((b: any, i: number) => (
                                            <li key={i} style={{ fontSize: 14, marginBottom: 2 }}>
                                                <span>{t('currency')}: <b>{b.currency}</b></span>
                                                {'  '}
                                                <span>{t('balance')}: <b>{Number(b.balance).toLocaleString()}</b></span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                    </Form.Item>
                    <Form.Item name="supplierName" label={t('supplierName')}>
                        <Input placeholder={t('supplier_name_if_direct') as string} />
                    </Form.Item>
                    <Form.Item name="supplierPhone" label={t('supplierPhone')}>
                        <Input placeholder={t('supplier_phone_if_direct') as string} />
                    </Form.Item>
                    <Form.Item name="paymentNumber" label={t('paymentNumber')} rules={[{ required: true, message: t('required') as string }]}>
                        <Input placeholder={t('payment_number') as string} />
                    </Form.Item>
                        <Form.Item name="date" label={t('date')} rules={[{ required: true, message: t('required') as string }]} >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="amount" label={t('amount')} rules={[{ required: true, message: t('required') as string }]}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="currency" label={t('currency')} rules={[{ required: true, message: t('required') as string }]}>
                        <Select
                            options={currencyList}
                            placeholder={t('currency')}
                            showSearch
                            optionFilterProp="label"
                        />
                    </Form.Item>
                    <Form.Item name="paymentMethod" label={t('paymentMethod')}>
                        <Select options={PAYMENT_METHODS} placeholder={t('payment_method')} allowClear />
                    </Form.Item>
                    <Form.Item name="reference" label={t('reference')}>
                        <Input placeholder={t('reference_number') as string} />
                    </Form.Item>
                    <Form.Item name="note" label={t('note')}>
                        <Input.TextArea placeholder={t('note') as string} autoSize />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                <Button type="primary" htmlType="submit" block>
                    {editSupplierPayment ? t('update_payment') : t('create_payment')}
                </Button>
            </Form.Item>
        </Form>
            {/* Hidden Payment Invoice for Print */}
            <div style={{ display: 'none' }}>
                {newPayment && (
                    <PaymentInvoice
                        ref={printRef}
                        type="supplier"
                        invoice={newPayment}
                        getPartyName={() => getSupplierName(newPayment.supplier, newPayment.supplierName)}
                        getPaymentMethodLabel={getPaymentMethodLabel}
                    />
                )}
            </div>
        </>
    );
};

export default SupplierPaymentForm;
